import { ChartsXAxis, ResponsiveChartContainer } from "@mui/x-charts";
import { LinePlot, MarkElementProps, MarkPlot } from "@mui/x-charts/LineChart";
import { theme } from "styles/theme";
import { Box, Stack, Typography } from "@mui/joy";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { selfCheckData, SelfCheckIdType } from "data/selfCheckData";

// Isomorphic layout effect to avoid first-paint flicker on client while being SSR-safe
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

// 말풍선 컴포넌트 (버블 패딩/깜빡임 개선)
const CustomTooltip = ({
  x,
  y,
  value,
  maxScore,
}: {
  x: number;
  y: number;
  value: number;
  maxScore: number;
}) => {
  const strongContentColor = theme.vars.palette.Content.Strong;
  const negativeContentColor = theme.vars.palette.Content.Negative;

  // Orientation based on value to avoid flip-flop near midline
  const isUpperHalf = value >= maxScore / 2;

  // Sizing constants
  const BUBBLE_BODY_WIDTH = value < 10 ? 33 : 41; // 1글자, 2글자 너비 차이
  const BUBBLE_BODY_HEIGHT = 34;
  const TAIL_WIDTH = 16;
  const TAIL_HEIGHT = 11;

  const SVG_WIDTH = BUBBLE_BODY_WIDTH + 1; // 42
  const SVG_HEIGHT = BUBBLE_BODY_HEIGHT + TAIL_HEIGHT; // 14+24+11
  const MARK_OFFSET = 8;

  const tailPathD =
    "M9.6641 9.50385C8.87246 10.6913 7.12754 10.6913 6.3359 9.50385L0 0L16 0L9.6641 9.50385Z";

  let rectY: number,
    pathTransform: string,
    contentTop: number,
    foreignObjectY: number;

  if (isUpperHalf) {
    foreignObjectY = y + MARK_OFFSET;
    rectY = TAIL_HEIGHT;
    pathTransform = `translate(${(SVG_WIDTH - TAIL_WIDTH) / 2}, ${TAIL_HEIGHT + 4}) scale(1, -1)`;
    contentTop = rectY + BUBBLE_BODY_HEIGHT / 2;
  } else {
    foreignObjectY = y - SVG_HEIGHT - MARK_OFFSET;
    rectY = 0;
    pathTransform = `translate(${(SVG_WIDTH - TAIL_WIDTH) / 2}, ${BUBBLE_BODY_HEIGHT - 4})`;
    contentTop = BUBBLE_BODY_HEIGHT / 2;
  }

  return (
    <foreignObject
      x={x - SVG_WIDTH / 2}
      y={foreignObjectY}
      width={SVG_WIDTH}
      height={SVG_HEIGHT}
      style={{ overflow: "visible", pointerEvents: "none" }}
    >
      <div
        style={{
          position: "relative",
          width: `${SVG_WIDTH}px`,
          height: `${SVG_HEIGHT}px`,
          willChange: "transform, top, left",
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
        }}
      >
        <svg
          width={SVG_WIDTH}
          height={SVG_HEIGHT}
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          xmlns="http://www.w3.org/2000/svg"
          style={{
            display: "block",
            position: "absolute",
            top: 0,
            left: 0,
            shapeRendering: "geometricPrecision",
          }}
        >
          <rect
            x="0.5"
            y={rectY}
            width={BUBBLE_BODY_WIDTH}
            height={BUBBLE_BODY_HEIGHT}
            rx="18"
            fill={strongContentColor}
          />
          <path
            d={tailPathD}
            fill={strongContentColor}
            transform={pathTransform}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            top: `${contentTop}px`,
            left: `${SVG_WIDTH / 2}px`,
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            color: negativeContentColor,
            fontWeight: 700,
            fontSize: "14px",
            lineHeight: "14px",
            margin: 0,
            padding: 0,
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </div>
      </div>
    </foreignObject>
  );
};

export default function SelfCheckLineChart({
  id,
  data,
  maxScore,
  placeholder,
  xLabel,
}: {
  id: SelfCheckIdType;
  data: number[];
  maxScore: number;
  placeholder?: string;
  xLabel?: string[];
}) {
  const strongContentColor = theme.vars.palette.Content.Strong;
  const negativeContentColor = theme.vars.palette.Content.Negative;

  const [selectedMarkIndex, setSelectedMarkIndex] = useState<number | null>(
    null
  );

  // ===== 너비 계산을 위한 상태 추가 =====

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (data.length > 0) {
      setSelectedMarkIndex(data.length - 1);
    }
  }, [data]);

  const handleMarkClick = (index: number) => {
    setSelectedMarkIndex(index);
  };

  const handleBandClick = (index: number) => {
    handleMarkClick(index);
  };

  const marginY = 20;
  const chartHeight = 200;
  const xLabelTranslateY = 10;

  // ===== 너비 계산 로직: CSS 비율 기반으로 처리 (측정 없이 초기 폭 확정) =====
  const MAX_VISIBLE_ITEMS = 5; // 화면에 표시할 최대 항목 수
  const innerWidthPercent =
    data.length > MAX_VISIBLE_ITEMS
      ? (data.length / MAX_VISIBLE_ITEMS) * 100
      : 100;

  const weekLabels = data.map((_, idx) => `W${idx + 1}`);

  const ranges = selfCheckData[id].result;
  const plotHeight = chartHeight - (marginY + xLabelTranslateY);
  const plotAreaMidY = marginY + plotHeight / 2;

  const numBands = Math.max(1, ranges.length);
  const equalBandHeight = plotHeight / numBands;
  const bandInfos = ranges.map((r, idx) => {
    const reversedIdx = numBands - 1 - idx;
    const top = marginY + reversedIdx * equalBandHeight;
    return { top, label: r.label };
  });

  const boundaryPositionsY: number[] = Array.from(
    { length: numBands + 1 },
    (_, i) => marginY + i * equalBandHeight
  );

  // ===== 초기 로딩 시 마지막으로 스크롤 (측정/재렌더 없이) =====
  useIsomorphicLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Scroll to end if content overflows
    if (el.scrollWidth > el.clientWidth) {
      el.scrollLeft = el.scrollWidth - el.clientWidth;
    }
  }, [data.length]);
  // ===== 수정 끝 =====

  // ===== PC: Enable horizontal scroll on mouse wheel when hovered =====
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Skip on touch/coarse pointer devices to preserve mobile behavior
    const isCoarsePointer =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    if (isCoarsePointer) return;

    const onWheel = (e: WheelEvent) => {
      if (!el) return;
      const canScroll = el.scrollWidth > el.clientWidth;
      if (!canScroll) return;

      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

      const dx = e.deltaX;
      const dy = e.deltaY;

      // If gesture already has horizontal intent, let browser handle it.
      if (Math.abs(dx) > Math.abs(dy)) return;

      const goingRight = dy > 0;
      const goingLeft = dy < 0;
      const canScrollRight = !atEnd;
      const canScrollLeft = !atStart;

      if ((goingRight && canScrollRight) || (goingLeft && canScrollLeft)) {
        e.preventDefault();
        el.scrollLeft += dy;
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel as unknown as EventListener);
    };
  }, [data.length]);
  // ===== end PC wheel scroll =====

  // ===== PC: Enable horizontal scroll by mouse drag when hovered =====
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Skip on touch/coarse pointer devices to preserve mobile behavior
    const isCoarsePointer =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    if (isCoarsePointer) return;

    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    let suppressClick = false;

    const onPointerDown = (e: PointerEvent) => {
      // Only left mouse button and mouse pointer
      const isMouse = (e as any).pointerType
        ? (e as any).pointerType === "mouse"
        : true;
      if (!isMouse || e.button !== 0) return;
      if (!el) return;
      if (el.scrollWidth <= el.clientWidth) return;

      isDown = true;
      moved = false;
      startX = e.clientX;
      startScrollLeft = el.scrollLeft;

      // Visual + selection prevention
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      el.scrollLeft = startScrollLeft - dx;
    };

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = "";
      el.style.userSelect = "";
      if (moved) {
        // Prevent the click that may follow a drag so it doesn't trigger band/mark selection
        suppressClick = true;
        setTimeout(() => {
          suppressClick = false;
        }, 0);
      }
    };

    const onClickCapture = (e: MouseEvent) => {
      if (suppressClick) {
        e.preventDefault();
        e.stopPropagation();
        suppressClick = false;
      }
    };

    el.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    el.addEventListener("pointerleave", endDrag);
    el.addEventListener("pointercancel", endDrag);
    el.addEventListener("click", onClickCapture, true);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
      el.removeEventListener("pointerleave", endDrag);
      el.removeEventListener("pointercancel", endDrag);
      el.removeEventListener("click", onClickCapture, true);
      // Cleanup styles
      if (el) {
        el.style.cursor = "";
        el.style.userSelect = "";
      }
    };
  }, [data.length]);
  // ===== end PC mouse drag scroll =====

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: chartHeight + xLabelTranslateY,
        overflow: "hidden",
      }}
      ref={chartContainerRef}
    >
      {/* 배경 밴드 및 라벨 (변경 없음) */}
      {boundaryPositionsY.map((y, i) => (
        <Box
          key={`boundary_y_${i}`}
          sx={{
            position: "absolute",
            width: "100%",
            height: "1px",
            top: `${y}px`,
            backgroundColor: theme.vars.palette.Border.Weak,
            zIndex: 0,
          }}
        />
      ))}
      {bandInfos.map((band, index) => (
        <Typography
          key={`band_label_${index}`}
          variant={"Footnote"}
          sx={{
            position: "absolute",
            top: `${band.top + 3}px`,
            left: 3,
            pointerEvents: "none",
            color: theme.vars.palette.Content.Weak,
            fontWeight: 400,
          }}
        >
          {band.label}
        </Typography>
      ))}
      <Typography
        variant={"Footnote"}
        sx={{
          position: "absolute",
          top: `${marginY + 11}px`,
          transform: "translateY(-50%)",
          right: 2,
          color: theme.vars.palette.Content.Weak,
          fontWeight: 400,
        }}
      >
        {maxScore}
      </Typography>

      {data.length > 0 ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE/Edge
            "&::-webkit-scrollbar": { display: "none" }, // WebKit
          }}
          ref={scrollRef}
        >
          {/* ===== 차트 내부 컨테이너 너비: 데이터 개수 기반 퍼센트로 고정 ===== */}
          <Box
            sx={{
              position: "relative",
              width: `${innerWidthPercent}%`,
              height: "100%",
            }}
          >
            {/* Chart */}
            <ResponsiveChartContainer
              margin={{
                top: marginY,
                left: 0,
                right: 0,
                bottom: marginY + xLabelTranslateY,
              }}
              series={[
                {
                  type: "line",
                  data,
                  color: strongContentColor,
                  curve: "linear",
                },
              ]}
              xAxis={[
                {
                  // ===== scaleType을 'band'로 변경하여 영역 중앙에 점을 위치시킴 =====
                  scaleType: "band",
                  data: xLabel || weekLabels,
                  id: "bottom-axis-id",
                },
              ]}
              yAxis={[{ min: 0, max: maxScore }]}
              disableAxisListener
            >
              <LinePlot
                sx={{
                  "& .MuiLineElement-root": {
                    stroke: strongContentColor,
                    strokeWidth: 2,
                  },
                }}
              />
              <MarkPlot
                slots={{
                  mark: (props: MarkElementProps) => {
                    const { x, y, dataIndex } = props;
                    const isSelected = selectedMarkIndex === dataIndex;
                    return (
                      <g>
                        <circle
                          cx={x}
                          cy={y}
                          r={isSelected ? 6 : 3}
                          fill={
                            isSelected
                              ? negativeContentColor
                              : strongContentColor
                          }
                          stroke={isSelected ? strongContentColor : "none"}
                          strokeWidth={isSelected ? 2 : 0}
                          onClick={() => handleMarkClick(dataIndex)}
                          style={{ cursor: "pointer" }}
                        />
                        {isSelected && (
                          <CustomTooltip
                            x={x}
                            y={y}
                            value={data[dataIndex]}
                            maxScore={maxScore}
                          />
                        )}
                      </g>
                    );
                  },
                }}
              />
              <ChartsXAxis
                position="bottom"
                axisId="bottom-axis-id"
                disableLine
                disableTicks
                tickLabelStyle={{
                  transform: `translateY(${xLabelTranslateY}px)`,
                  fontSize: "12px",
                  fill: theme.vars.palette.Content.Strong,
                  fontWeight: 400,
                }}
              />
            </ResponsiveChartContainer>

            {/* Overlay clickable bands to select tooltip when clicking entire X regions (e.g., W1, W2) */}
            {(() => {
              const labelsLength = (xLabel || weekLabels).length;
              return (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 10,
                    display: "flex",
                  }}
                >
                  {Array.from({ length: labelsLength }).map((_, i) => (
                    <Box
                      key={`click-band-${i}`}
                      onClick={() => handleBandClick(i)}
                      sx={{
                        flex: 1,
                        height: "100%",
                        cursor: "pointer",
                        backgroundColor: "transparent",
                      }}
                    />
                  ))}
                </Box>
              );
            })()}
          </Box>
        </Box>
      ) : (
        <Stack
          sx={{ width: "100%", height: "100%", pb: "20px" }}
          justifyContent="center"
          alignItems={"center"}
        >
          <Typography sx={{ fontSize: "14px", opacity: 0.3, fontWeight: 500 }}>
            {placeholder}
          </Typography>
        </Stack>
      )}
    </div>
  );
}
